import { useState } from "react";
import { useSearchParams } from "react-router";

import { useScopeParams } from "@/hooks/useScopeParams";
import { PageTemplate } from "@/components/Templates/PageTemplate";
import {
  WaveformDrawer,
  type WaveformDrawerStudyMeta,
} from "@/components/Waveforms";

/**
 * ECG — the single home for ECG. The view is the numeric interval grid, pivoted
 * to one row per subject-visit with every ECG parameter (HR / PR / QRS / QT /
 * QTcF, the global summaries plus per-lead values) as its own column, served by
 * the `ecg_intervals` dataset.
 *
 * Each pivoted row carries `imaging_study_id` (the matching Clario ECG exam).
 * Clicking a row opens the single-record ECG waveform viewer via the shared
 * `WaveformDrawer` — the exact `?waveform=<imaging_study_id>` mechanism used by
 * the Imaging Studies screen.
 *
 * NOTE: the multi-subject "ECG Trends" / compare-subjects view was intentionally
 * removed (UAT 2026-07-20) and is NOT reintroduced here — the drawer is opened
 * with `showCompare={false}` so it is strictly the per-record viewer. The
 * `measures/ecg-trends` deep-link route still renders the standalone viewer for
 * anyone with a saved link.
 */
export default function ECG({ singleSubjectId }: { singleSubjectId?: string }) {
  const { id } = useScopeParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const openStudyId = searchParams.get("waveform");

  // Metadata for the drawer title, captured from the clicked row.
  const [openStudyMeta, setOpenStudyMeta] = useState<
    WaveformDrawerStudyMeta | undefined
  >(undefined);

  const wikiLink = singleSubjectId
    ? "/wiki/guides/exploration-singlesubject-ecg"
    : "/wiki/guides/exploration-measures-ecg";

  const setWaveformParam = (studyId: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (studyId) {
          next.set("waveform", studyId);
        } else {
          next.delete("waveform");
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <>
      <PageTemplate
        title="Measures: ECG"
        tooltipText="Explore ECG interval data (HR, PR, QRS, QT, QTcF) and the cardiologist overread (result, findings, reader) per subject per visit, create data subsets, view visualizations, and download data. Click a row with an ECG exam to open the waveform viewer."
        type="ecg_intervals"
        constants={{
          wikiLink,
          idLink: (subjectId: string) =>
            `/${id}/subjects/overview/${subjectId}/ecg`,
        }}
        subjectId={singleSubjectId}
        onRow={(record) => {
          const studyId = record.imaging_study_id as string | undefined;
          if (!studyId) {
            return {};
          }
          return {
            style: { cursor: "pointer" },
            onClick: () => {
              setOpenStudyMeta({
                subject: record.subject as string | null,
                visit_type: record.visit_type as string | null,
                study_date: record.assessment_date as string | null,
                modality: "ECG",
              });
              setWaveformParam(studyId);
            },
          };
        }}
      />
      <WaveformDrawer
        open={Boolean(openStudyId)}
        onClose={() => {
          setOpenStudyMeta(undefined);
          setWaveformParam(null);
        }}
        imagingStudyId={openStudyId}
        studyMeta={openStudyMeta}
        showCompare={false}
      />
    </>
  );
}
